import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Plus, Trash2 } from 'lucide-react';
import { maskDob, validateDob } from '@/lib/swissValidators';

const DEFAULTS = {
  title: 'Kinder (für Familienzulagen)',
  add: 'Kind hinzufügen',
  name: 'Vorname',
  birth: 'Geburtsdatum',
  edu: 'In Ausbildung (ab 16)',
  hint: 'Die Familienzulagen berechnet das Lohnsystem automatisch.',
};

export const ChildrenEditor = ({ value = [], onChange, labels = {}, dark = false }) => {
  const t = { ...DEFAULTS, ...labels };
  const list = Array.isArray(value) ? value : [];
  const update = (i, patch) => onChange(list.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  const add = () => onChange([...list, { first_name: '', birth_date: '', in_education: false }]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  const inputCls = dark ? 'bg-neutral-950 border-neutral-700 text-white' : '';

  return (
    <div className="space-y-2 pt-1" data-testid="children-editor">
      <div className="flex items-center justify-between">
        <span className={`text-sm font-medium ${dark ? 'text-neutral-300' : ''}`}>{t.title}</span>
        <Button type="button" variant="outline" size="sm" onClick={add} data-testid="child-add-btn">
          <Plus size={14} className="mr-1" />{t.add}
        </Button>
      </div>
      {list.map((c, i) => {
        const err = c.birth_date ? validateDob(c.birth_date) : '';
        return (
          <div key={i} className="space-y-1" data-testid={`child-row-${i}`}>
            <div className="flex items-center gap-2">
              <Input
                placeholder={t.name}
                value={c.first_name || ''}
                className={inputCls}
                onChange={(e) => update(i, { first_name: e.target.value })}
                data-testid={`child-name-${i}`}
              />
              <Input
                placeholder="TT.MM.JJJJ"
                inputMode="numeric"
                value={c.birth_date || ''}
                className={`${inputCls} ${err ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                onChange={(e) => update(i, { birth_date: maskDob(e.target.value) })}
                data-testid={`child-birth-${i}`}
              />
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)} data-testid={`child-remove-${i}`}>
                <Trash2 size={15} className="text-red-500" />
              </Button>
            </div>
            <label className={`flex items-center gap-2 text-xs ${dark ? 'text-neutral-400' : 'text-muted-foreground'}`}>
              <input
                type="checkbox"
                checked={!!c.in_education}
                onChange={(e) => update(i, { in_education: e.target.checked })}
                data-testid={`child-edu-${i}`}
              />
              {t.edu}
            </label>
            {err ? <p className="text-xs text-red-500" data-testid={`child-birth-error-${i}`}>{t.birth}: TT.MM.JJJJ</p> : null}
          </div>
        );
      })}
      {list.length > 0 && (
        <p className={`text-xs ${dark ? 'text-neutral-500' : 'text-muted-foreground'}`}>{t.hint}</p>
      )}
    </div>
  );
};

// Alle Kinder gültig: entweder leer (kein Name & kein Datum) oder Name + gültiges Datum.
export function childrenValid(list = []) {
  return (list || []).every((c) => {
    const hasName = !!(c.first_name && c.first_name.trim());
    const hasDate = !!(c.birth_date && c.birth_date.trim());
    if (!hasName && !hasDate) return true;
    return hasName && hasDate && !validateDob(c.birth_date);
  });
}
